/**
 * Registration phone verification.
 *
 * The partner cannot move past the contact step until the mobile number they
 * gave us answers an SMS challenge. `verifyRegistrationOtp` returns a
 * short-lived token that the final submit must carry, so the gate holds even
 * if the wizard state is tampered with.
 */

import { apiClient } from 'shared/lib/apiClient';
import type { SupportedLanguage } from 'shared/i18n/i18n';

export interface SendOtpResult {
  challengeId: string;
  /** Seconds until the code expires. */
  otpExpiresIn: number;
  /** Masked recipient, safe to display. */
  sentTo: string;
}

export interface VerifyOtpResult {
  ok: boolean;
  verificationToken: string;
}

export type OtpErrorCode =
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_LOCKED'
  | 'OTP_RESEND_COOLDOWN'
  | 'OTP_NOT_FOUND'
  | 'OTP_SEND_FAILED'
  | 'UNKNOWN';

/** Map an axios failure onto one of the coded OTP outcomes. */
export const otpErrorCode = (err: unknown): OtpErrorCode => {
  const code = (
    err as { response?: { data?: { code?: string } } }
  )?.response?.data?.code;
  const known: OtpErrorCode[] = [
    'OTP_INVALID',
    'OTP_EXPIRED',
    'OTP_LOCKED',
    'OTP_RESEND_COOLDOWN',
    'OTP_NOT_FOUND',
    'OTP_SEND_FAILED',
  ];
  return known.find((k) => k === code) ?? 'UNKNOWN';
};

export async function sendRegistrationOtp(
  phoneNumber: string,
  language: SupportedLanguage,
): Promise<SendOtpResult> {
  const { data } = await apiClient.post<SendOtpResult>('/onboarding/otp/send', {
    phoneNumber,
    language,
  });
  return data;
}

export async function resendRegistrationOtp(
  challengeId: string,
): Promise<SendOtpResult> {
  const { data } = await apiClient.post<SendOtpResult>(
    '/onboarding/otp/resend',
    { challengeId },
  );
  return data;
}

export async function verifyRegistrationOtp(
  challengeId: string,
  code: string,
): Promise<VerifyOtpResult> {
  const { data } = await apiClient.post<VerifyOtpResult>(
    '/onboarding/otp/verify',
    { challengeId, code },
  );
  return data;
}
